import {
    RecurringSchedule,
    TransferSchedule,
    TransferScheduleMonthBoundary,
    TransferScheduleMonthlyByDateDetails,
    TransferScheduleType,
    TransferScheduleWeekDay,
    TransferScheduleWeeklyDetails,
} from "@/shared/types";
import moment from "moment-timezone";
import { addBusinessDays } from "@/shared/businessDayUtil";
import { useBankHolidayStore } from "@/employer/store/bankHoliday";
import * as dateHelper from "@/shared/dateHelper";

/**
 * Pre-requisite:
 * bankHolidayStore.loadBankHolidays(currentYear)
 * bankHolidayStore.loadBankHolidays(currentYear + 1)
 *
 * Calculates the next valid business day, removing bank holidays and weekends
 * @returns moment of the valid day.
 */
export function firstValidStartDateFromNow (): moment.Moment {
    // before 2pm Central, contributions for T+1
    // after 2pm Central, contributions for T+2

    const bankHolidayStore = useBankHolidayStore();
    const currentMomentCST = dateHelper.todayCST();
    const currentYear = currentMomentCST.year();

    // pre-requisite, loading bank holidays
    if (bankHolidayStore.bankHolidaysInYearLoaded(currentYear) === false
        || bankHolidayStore.bankHolidaysInYearLoaded(currentYear + 1) === false
    ) {
        throw new Error("Bank holidays are not loaded, first valid date from now cannot be calculated");
    }

    const holidays = federalHolidays();

    const result = currentMomentCST.hours() > 13
        ? addBusinessDays(holidays, currentMomentCST.clone(), 2)
        : addBusinessDays(holidays, currentMomentCST.clone(), 1);
    return moment(result);
}

export function federalHolidays (): string[] {
    const currentMomentCST = dateHelper.todayCST();
    const currentYear = currentMomentCST.year();
    const bankHolidayStore = useBankHolidayStore();

    return [
        ...bankHolidayStore.bankHolidaysInYear(currentYear),
        ...bankHolidayStore.bankHolidaysInYear(currentYear + 1),
    ];
}

export function isBusinessDay (date: moment.Moment): boolean {
    const dow = date.isoWeekday();
    if (dow === 6 || dow === 7) {
        return false; // weekend
    }

    // sorted array of holidays
    const holidays = federalHolidays();

    for (const str of holidays) {
        const temp = moment(str);

        if (date.isSame(temp)) {
            // holiday
            return false;
        }

        if (temp.isAfter(date)) {
            // short circuit
            // the holiday being checked is after our date,
            // therefore no holidays in this list will be that date
            return true;
        }
    }
    return true;
}

export function getScheduleDescription (schedule: TransferSchedule): string {
    if (!schedule) {
        return "";
    }
    if (schedule.scheduleType === TransferScheduleType.MonthlyByDate) {
        return schedule.monthlyByDateDetails.length === 0
            ? ""
            : buildMonthlyByDateDescription(schedule);
    } else if (schedule.scheduleType === TransferScheduleType.MonthlyByDay) {
        return schedule.monthlyByDayDetails.length === 0
            ? ""
            : buildMonthlyByDayDescription(schedule);
    } else if (schedule.scheduleType === TransferScheduleType.Weekly) {
        return schedule.weeklyDetails.length === 0
            ? ""
            : buildWeeklyDescription(schedule);
    } else if (schedule.scheduleType === TransferScheduleType.OneTime) {
        return buildOneTimeDescription(schedule);
    }
    // else schedule type is none
    return "";
}

function buildOneTimeDescription (schedule: TransferSchedule): string {
    return moment(schedule.scheduleStartDate).format("MMM D, YYYY");
}

function buildMonthlyByDateDescription (schedule: TransferSchedule): string {
    return "The " + buildDatesDescription(schedule.monthlyByDateDetails)
        + buildFrequencyTypeDescription(schedule.scheduleInterval, "month");
}

function buildMonthlyByDayDescription (schedule: TransferSchedule): string {
    const singleDetail = schedule.monthlyByDayDetails[0];

    return "The " + buildMonthDayDescription(singleDetail.dayOfWeek, singleDetail.monthBoundary)
        + buildFrequencyTypeDescription(schedule.scheduleInterval, "month");
}

function buildWeeklyDescription (schedule: TransferSchedule): string {
    return buildWeekDaysDescription(schedule.weeklyDetails)
        + buildFrequencyTypeDescription(schedule.scheduleInterval, "week");
}

function buildFrequencyTypeDescription (frequency: number, name: string): string {
    if (frequency === 1) {
        return `, every ${name}`;
    } else return `, every ${frequency} ${name}s`;
}

function buildMonthDayDescription (dayOfWeek: TransferScheduleWeekDay,
                                   frequency: TransferScheduleMonthBoundary): string {
    return `${getBoundary(frequency)} ${getWeekDay(dayOfWeek)}`;
}

function buildWeekDaysDescription (details: TransferScheduleWeeklyDetails[]): string {
    const days = details.map(x => x.dayOfWeek);
    let resultString = "";
    days.sort(dayCompare);
    days.forEach(day => {
        if (resultString === "") {
            resultString = getWeekDay(day);
        } else if (days.indexOf(day) !== days.length - 1) {
            resultString += `, ${getWeekDay(day)}`;
        } else {
            resultString += ` & ${getWeekDay(day)}`;
        }
    });
    return resultString;
}

function buildDatesDescription (dates: TransferScheduleMonthlyByDateDetails[]): string {
    let resultString = "";
    // copy, because sorting mutates the array
    const datesCopy = [...dates];
    datesCopy.sort(dateCompare);
    datesCopy.forEach(date => {
        if (resultString === "") {
            resultString = getFormattedDate(date);
        } else if (datesCopy.indexOf(date) !== datesCopy.length - 1) {
            resultString += `, ${getFormattedDate(date)}`;
        } else {
            resultString += ` & ${getFormattedDate(date)}`;
        }
    });
    return resultString;
}

function dateCompare
(a: TransferScheduleMonthlyByDateDetails, b: TransferScheduleMonthlyByDateDetails): number {
    if (a.isLastDay) {
        return 1;
    }

    if (b.isLastDay) {
        return -1;
    }

    if (a.dayOfMonth < b.dayOfMonth) {
        return -1;
    } else {
        return 1;
    }
}

function dayCompare
(a: TransferScheduleWeekDay, b: TransferScheduleWeekDay): number {
    if (weekDayIndex(a) < weekDayIndex(b)) {
        return -1;
    }

    return 1;
}

function getFormattedDate (date: TransferScheduleMonthlyByDateDetails) {
    if (date.isLastDay) {
        return "last day";
    } else {
        return moment("01-" + date.dayOfMonth, "MM-DD").format("Do");
    }
}

function weekDayIndex (day: TransferScheduleWeekDay): number {
    return Object.values(TransferScheduleWeekDay).indexOf(day);
}

function getWeekDay (day: TransferScheduleWeekDay): string {
    const index = weekDayIndex(day);
    return Object.keys(TransferScheduleWeekDay)[index];
}

function getBoundary (boundary: TransferScheduleMonthBoundary): string {
    return Object.keys(TransferScheduleMonthBoundary)[
        Object.values(TransferScheduleMonthBoundary).indexOf(boundary)].toLowerCase();
}


const isWeekday = {
    getMessage (fieldName) {
        return `${fieldName} cannot be on a weekend`;
    },
    validate (date) {
        return moment(date).day() !== 0 && moment(date).day() !== 6;
    }
};

const isNotFederalHoliday = {
    getMessage (fieldName) {
        return `${fieldName} cannot be on a federal holiday`;
    },
    validate (date, args) {
        if (args[0] === "null") {
            return true;
        }
        return !args.map(m => moment(m).format("YYYY-MM-DD")).includes(date);
    }
};

const validateDateIsAfter = {
    getMessage (toField, fromField) {
        return toField + " must be after " + moment(fromField[0]).format("MM/DD/YYYY");
    },
    validate (toDate, args) {
        if (args[0] === "null") {
            return true;
        }
        return moment(toDate).isSameOrAfter(args[0]);
    }
};

export const validationObjects = { isWeekday, isNotFederalHoliday, validateDateIsAfter };

const everyWeekRecurringSchedule: RecurringSchedule = {
    scheduleType: TransferScheduleType.Weekly,
    scheduleInterval: 1,
    dates: [],
    days: [],
    dayOfWeek: TransferScheduleWeekDay.None,
    scheduleMonthBoundary: TransferScheduleMonthBoundary.None,
};

export const defaultValues = { everyWeekRecurringSchedule };